import React, { useEffect, useState } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useFormik } from "formik";
import Select from 'react-select';
import TextInput from "../../common/textfield/TextInput.jsx";
import TextArea from "../../common/textfield/TextArea.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../common/Button/index.js";
import { failed, success } from "../../common/Toastify.jsx";
import {
  FormikObj,
  FormikObjEdit,
  fileTypes,
  deliveryTypes,
  protocols,
  hl7Protocols,
  hl7Templates,
  hl7FileTypes,
  resolveFileTypeSelection,
  buildHl7SecretPayload,
  PEM_CONVERSION_HINT,
  SINGLE_XML_FORMAT_MESSAGE,
} from "./Constant.js";
import { StoredCertificate, hintStyle, pemStyle } from "./CertificateDisplay.js";
import { Tooltip } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { createReportDelivery, getReportDeliveryById, updateReportDelivery } from "../../api/ReportDeliveries/index.js";
import { useOrgQuery } from "../../../hooks/ReactQueryHooks/useOrgQuery.js";

function EditReportDelivery(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const adminOrgs = useSelector((state) => state?.auth?.user?.organizations)
  const [reportDeliveryData, setReportDeliveryData] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [orgId, setOrgId] = useState(location?.state?.orgId || adminOrgs[0].id);
  const [btnLoading, setBtnLoading] = useState(false);
  const [isFax, setIsFax] = useState(false);
  const [isHl7, setIsHl7] = useState(false);
  const [storedCertificates, setStoredCertificates] = useState(null);

  useOrgQuery({
      onSuccess: (data) => {
          setOrganizations(data?.map((org) => ({ label: org?.name, value: org?.id })))
      },
      refetchOnWindowFocus: false,
  })

  const handleOrgChange = (event) => {
      setOrgId(event.value);
      formik.setFieldValue("orgId", event.value)
  }

  const getSelected = (items, value) => {
    return items?.find(item => item.value == value);
  }

  const formik = useFormik({
    ...(props.new ? FormikObj : FormikObjEdit),
    onSubmit: async (values) => {
      const payload = { ...values };
      payload.name = payload.name?.trim();
      payload.hostName = payload.hostName?.trim();
      payload.template = payload.template?.trim();
      payload.messageSpec = payload.messageSpec?.trim();

      if (payload.type === "FAX") {
        payload.hostName = "";
        payload.protocol = "";
        payload.userName = "";
        payload.secret = "";
        payload.template = "";
        payload.target = payload.target?.trim();
        payload.fileType = ["PDF"];
      } else if (payload.type === "HL7") {
        payload.target = null;
        payload.userName = "";
        payload.messageSpec = "";
        // Omitting the secret leaves the stored bundle untouched.
        payload.secret = values.replacingCertificates ? buildHl7SecretPayload(values) : "";
        // Hidden in UI; Step Function gates HL7 build on fileType containing "HL7".
        payload.fileType = [...hl7FileTypes];
      } else {
        payload.target = payload.target?.trim();
        payload.userName = payload.userName?.trim();
        payload.secret = payload.secret?.trim();
        payload.template = "";
      }

      delete payload.replacingCertificates;
      delete payload.clientCertificate;
      delete payload.clientPrivateKey;
      delete payload.caCertificate;

      setBtnLoading(true);
      try {
        ((props.new) ? createReportDelivery(payload) : updateReportDelivery(payload)).then(res => {
          if (res.status === true) {
            navigate("/app/report-deliveries", {state: {orgId}})
            success(res.message);
          } else {
            failed(res.error);
          }
        })
        .catch(err => {
          failed(err?.response?.data?.message || err?.response?.data?.error || err.message);
        })
        .finally(() => setBtnLoading(false));
    }
    catch(err) {
      failed(err?.response?.data?.message || err?.response?.data?.error || err.message);
    }
  }});

  const clearCertificateFields = () => {
    formik.setFieldValue("replacingCertificates", false);
    formik.setFieldValue("clientCertificate", "");
    formik.setFieldValue("clientPrivateKey", "");
    formik.setFieldValue("caCertificate", "");
  };

  const handleTypeChange = (e) => {
    const value = e.value;
    setIsFax(value === "FAX");
    setIsHl7(value === "HL7");

    if (value === "FAX") {
      formik.setFieldValue("hostName", "");
      formik.setFieldValue("protocol", "");
      formik.setFieldValue("userName", "");
      formik.setFieldValue("secret", "");
      formik.setFieldValue("template", "");
      clearCertificateFields();
      formik.setFieldValue("fileType", ["PDF"]);
    } else if (value === "HL7") {
      formik.setFieldValue("protocol", "MLLP");
      formik.setFieldValue("target", "");
      formik.setFieldValue("userName", "");
      formik.setFieldValue("secret", "");
      formik.setFieldValue("messageSpec", "");
      formik.setFieldValue("replacingCertificates", !storedCertificates?.keyConfigured);
      formik.setFieldValue("fileType", [...hl7FileTypes]);
    } else {
      formik.setFieldValue("template", "");
      clearCertificateFields();
      // Restore a normal selectable default when leaving HL7 (file type was hidden).
      if (formik.values.type === "HL7" || !formik.values.fileType?.length || formik.values.fileType.includes("HL7")) {
        formik.setFieldValue("fileType", ["PDF"]);
      }
    }

    formik.setFieldValue("target", "");
    formik.setFieldValue("type", value)
  }

  useEffect(() => {
    if (!props.new && location?.state?.id && location?.state?.orgId) {
      const { id, orgId: navOrgId } = location.state;
      setOrgId(navOrgId);

      getReportDeliveryById(id, navOrgId)
        .then(res => {
          if (res?.status && res?.data) {
            const record = Array.isArray(res.data) ? res.data[0] : res.data;

            if (!record) return;

            setReportDeliveryData(record);
            setIsFax(record.type === "FAX");
            setIsHl7(record.type === "HL7");
            setStoredCertificates(record.certificates ?? null);

            const loadedFileType = Array.isArray(record.fileType)
              ? record.fileType
              : (record.fileType ? [record.fileType] : ["PDF"]);

            formik.setValues({
              id: record.id ?? "",
              type: record.type ?? "",
              protocol: record.protocol ?? "",
              hostName: record.hostName ?? "",
              target: record.target ?? "",
              userName: record.userName ?? "",
              secret: record.secret ?? "",
              template: record.template ?? "",
              replacingCertificates: record.type === "HL7" && !record.certificates?.keyConfigured,
              clientCertificate: "",
              clientPrivateKey: "",
              caCertificate: "",
              fileType: record.type === "HL7" ? [...hl7FileTypes] : loadedFileType,
              name: record.name ?? "",
              orgId: record.orgId ?? navOrgId,
              messageSpec: record.messageSpec ?? "",
            });
          }
        })
        .catch(err => failed(err?.response?.data?.message || err?.message || "Failed to load"));
    } else {
      formik.setFieldValue("orgId", orgId);
    }
  }, []);

  const protocolOptions = isHl7 ? hl7Protocols : protocols;

  return (
    <section className="common-listing">
      <div className="heading-wrap mb-3">
        <Tooltip title="Go back">
          <div
            style={{ marginRight: "1rem", cursor: "pointer" }}
            onClick={() => navigate("/app/report-deliveries", {state: {orgId }})}
          >
            <ArrowBack />
          </div>
        </Tooltip>
        <h1>Report Delivery Details</h1>
      </div>
      <div className="custom-card p-4">
        <form className="common-form border-fields"
          onSubmit={formik.handleSubmit}>

        { organizations.length > 1 &&
            <Row style = {{marginBottom:'30px', marginTop: '20px'}}>
              <Col xs lg="2">
                <label className="searchSelectLabel">
                  Organization<span style={{ color: "red" }}> *</span>{" "}
                </label>
              </Col>
              <Col xs lg="4">
              {props.new
                ?
                  <Select
                  keyField={"orgId"}
                  formik={formik}
                  label={"Select Organization"}
                  selectLabel="Select Organization"
                  options={organizations}
                  isLoading={organizations?.length >= 1 ? false : true}
                  readOnly={organizations?.length >= 1 ? false : true}
                  hideRequired={true}
                  onChange={handleOrgChange}
                  className="search-select"
                  classNamePrefix="select"
                  placeholder="Select Organization..."
                  value={getSelected(organizations, (formik.values?.orgId || orgId))}
                  />
                : <span>{organizations.find(org => org.value === reportDeliveryData?.orgId)?.label}</span>
              }
              </Col>
            </Row>
        }
        <Row style = {{marginBottom:'10px', marginTop: '0px'}}>
          <Col xs lg="2">
                <label className="searchSelectLabel">
                  Delivery Name<span style={{ color: "red" }}> *</span>{" "}
                </label>
          </Col>
          <Col xs lg="4">
                <TextInput
                      keyField={"name"}
                      formik={formik}
                      placeholder={"Delivery Name"}
                      hideRequired={true}
                      required={true}
                      style={{width: "80%"}}
                />
            </Col>
        </Row>
        <Row style = {{marginBottom:'30px', marginTop: '0px'}}>
          <Col xs lg="2">
                <label className="searchSelectLabel">
                  Delivery Type<span style={{ color: "red" }}> *</span>{" "}
                </label>
          </Col>
          <Col xs lg="4">
              <Select
                  keyField={"type"}
                  formik={formik}
                  label={"Select Delivery Type"}
                  selectLabel="Select Delivery Type"
                  options={deliveryTypes}
                  isLoading={deliveryTypes?.length > 1 ? false : true}
                  readOnly={deliveryTypes?.length > 1 ? false : true}
                  onChange={handleTypeChange}
                  className="search-select"
                  classNamePrefix="select"
                  placeholder="Select Delivery Type..."
                  value={getSelected(deliveryTypes, formik.values.type)}
                  />
            </Col>
        </Row>
        {!isFax && (<Row style = {{marginBottom:'30px', marginTop: '20px'}}>
          <Col xs lg="2">
                <label className="searchSelectLabel">
                  Protocol{isHl7 && <span style={{ color: "red" }}> *</span>}
                </label>
          </Col>
          <Col xs lg="4">
              <Select
                  keyField={"protocol"}
                  formik={formik}
                  label={"Select Protocol"}
                  selectLabel="Select Protocol"
                  options={protocolOptions}
                  isLoading={protocolOptions?.length > 1 ? false : true}
                  readOnly={protocolOptions?.length > 1 ? false : true}
                  onChange={(value) => formik.setFieldValue("protocol",value.value)}
                  className="search-select"
                  classNamePrefix="select"
                  isDisabled={(formik.values.type == "FAX")}
                  placeholder="Select Delivery Protocol..."
                  value={getSelected(protocolOptions, formik.values.protocol)}
                  />
            </Col>
        </Row>)}
        {isHl7 && (
          <Row style={{ marginBottom: "30px", marginTop: "20px" }}>
            <Col xs lg="2">
              <label className="searchSelectLabel">
                HL7 Template<span style={{ color: "red" }}> *</span>
              </label>
            </Col>
            <Col xs lg="4">
              <Select
                keyField={"template"}
                formik={formik}
                label={"Select HL7 Template"}
                selectLabel="Select HL7 Template"
                options={hl7Templates}
                onChange={(value) => formik.setFieldValue("template", value.value)}
                className="search-select"
                classNamePrefix="select"
                placeholder="Select HL7 Template..."
                value={getSelected(hl7Templates, formik.values.template)}
              />
              {formik.errors.template && formik.touched.template && (
                <div className="error-message">{formik.errors.template}</div>
              )}
            </Col>
          </Row>
        )}
        {!isFax && (<Row>
          <Col xs lg="2">
            <label className="searchSelectLabel">
              Host Name{isHl7 && <span style={{ color: "red" }}> *</span>}
            </label>
          </Col>
          <Col>
                    <TextInput
                      keyField={"hostName"}
                      formik={formik}
                      placeholder={isHl7 ? "host:port (e.g. hl7.example:2575)" : "Host Name"}
                      hideRequired={true}
                      required={false}
                      disabled={(formik.values.type == "FAX")}
                      style={{width: "50%"}}
          />
          </Col>
        </Row>)}
        {isHl7 && (
          <Row style={{ marginBottom: "30px", marginTop: "20px" }}>
            <Col xs lg="2">
              <label className="searchSelectLabel">
                mTLS Certificates{formik.values.replacingCertificates && <span style={{ color: "red" }}> *</span>}
              </label>
            </Col>
            <Col>
              {formik.values.replacingCertificates ? (
                <>
                  <div style={hintStyle}>
                    Open each file in a text editor and paste its full contents, including the BEGIN and END lines.
                    {" "}{PEM_CONVERSION_HINT}
                  </div>
                  <TextArea
                    label="Client certificate (client.crt)"
                    keyField={"clientCertificate"}
                    formik={formik}
                    hideRequired={true}
                    rows={8}
                    placeholder={"-----BEGIN CERTIFICATE-----"}
                    style={pemStyle}
                  />
                  <div style={hintStyle}>Ours. Presented to the receiving system during the handshake.</div>
                  <TextArea
                    label="Client private key (client.key)"
                    keyField={"clientPrivateKey"}
                    formik={formik}
                    hideRequired={true}
                    rows={8}
                    placeholder={"-----BEGIN PRIVATE KEY-----"}
                    style={pemStyle}
                  />
                  <div style={hintStyle}>Ours. Never shared, and not shown again once saved.</div>
                  <TextArea
                    label="CA certificate (ca.crt)"
                    keyField={"caCertificate"}
                    formik={formik}
                    hideRequired={true}
                    rows={8}
                    placeholder={"-----BEGIN CERTIFICATE-----"}
                    style={pemStyle}
                  />
                  <div style={hintStyle}>
                    Theirs. Used to verify the receiving system. Paste the full chain if you were given one.
                  </div>
                  {storedCertificates?.keyConfigured && (
                    <Button variant="secondary" onClick={clearCertificateFields}>
                      Keep existing certificates
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <StoredCertificate
                    label="Client certificate (client.crt)"
                    pem={storedCertificates?.cert}
                    metadata={storedCertificates?.certificate}
                    description="Ours. Presented to the receiving system during the handshake."
                  />
                  <div style={{ marginBottom: "1rem" }}>
                    <label className="searchSelectLabel">Client private key (client.key)</label>
                    <div style={hintStyle}>Stored securely. Never displayed after saving.</div>
                  </div>
                  <StoredCertificate
                    label="CA certificate (ca.crt)"
                    pem={storedCertificates?.ca}
                    metadata={storedCertificates?.caCertificate}
                    description="Theirs. Used to verify the receiving system."
                  />
                  <Button
                    variant="secondary"
                    onClick={() => formik.setFieldValue("replacingCertificates", true)}
                  >
                    Replace certificates
                  </Button>
                </>
              )}
            </Col>
          </Row>
        )}
        {!isFax && !isHl7 && (<Row>
          <Col xs lg="2">
            <label className="searchSelectLabel">
              Username
            </label>
          </Col>
          <Col>
                    <TextInput
                      keyField={"userName"}
                      formik={formik}
                      placeholder={"Username"}
                      hideRequired={true}
                      required={false}
                      style={{width: "50%"}}
          />
          </Col>
        </Row>)}
        {!isFax && !isHl7 && (<Row>
          <Col xs lg="2">
            <label className="searchSelectLabel">
              Secret
            </label>
          </Col>
          <Col>
                    <TextInput
                      keyField={"secret"}
                      formik={formik}
                      placeholder={"Secret"}
                      hideRequired={true}
                      required={false}
                      type="password"
                      style={{width: "50%"}}
          />
          </Col>
        </Row>)}
        {!isHl7 && (
        <Row>
          <Col xs lg="2">
            {!isFax ? <label className="searchSelectLabel">
              Target<span style={{ color: "red" }}> *</span>{" "}
            </label> : <label className="searchSelectLabel">
              FAX Number (11 - digits):
            </label>}
          </Col>
          <Col>
                    {!isFax ? <TextInput
                      keyField={"target"}
                      formik={formik}
                      placeholder={"Report Target"}
                      hideRequired={false}
                      required={true}
                      style={{width: "50%"}}
                    /> :
                    <TextInput
                      keyField={"target"}
                      formik={formik}
                      placeholder={"Fax number"}
                      hideRequired={false}
                      required={true}
                      style={{width: "200px"}}
                    />}
          </Col>
        </Row>
        )}
        {!isHl7 && (
        <Row style = {{marginBottom:'30px', marginTop: '20px'}}>
          <Col xs lg="2">
                <label className="searchSelectLabel">
                  File Type<span style={{ color: "red" }}> *</span>{" "}
                </label>
          </Col>
          <Col xs lg="4">
              <Select
                  isMulti
                  options={isFax ? fileTypes.filter(o => o.value === "PDF") : fileTypes}
                  className="search-select"
                  classNamePrefix="select"
                  placeholder="Select File Type..."
                  onChange={values => {
                    const { fileType, rejected } = resolveFileTypeSelection(
                      formik.values.fileType,
                      values || []
                    );
                    formik.setFieldValue("fileType", fileType);
                    if (rejected) {
                      formik.setFieldError("fileType", SINGLE_XML_FORMAT_MESSAGE);
                    } else {
                      formik.setFieldError("fileType", undefined);
                    }
                  }}
                  value={fileTypes.filter(opt => formik.values.fileType?.includes(opt.value))}
                  />
              {formik.errors.fileType && formik.touched.fileType && (
                <div className="error-message">{formik.errors.fileType}</div>
              )}
            </Col>
        </Row>
        )}
        <div className="btn-wrap" style={{ display: "flex", marginTop: "45px" }}>
             <Button
              onClick={() =>
                  navigate("/app/report-deliveries", {state: {orgId }})
              }
              variant="secondary"
              title="Cancel"
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={btnLoading}>
              Save
            </Button>
          </div>
        </form>
      </div>
    </section>
  )
}

export default EditReportDelivery;
